import {
  BlanklineType,
  BorderWeight,
  CalculatedKPIValues,
  CellFormat,
  CodeOrDescription,
} from '@services/api/deckManagement/graphql/schema/API';
import { z } from 'zod';

import {
  BaseDeckManagementEntry,
  CommentSchema,
  createChangeSetOperations,
  KPIDriversInputSchema,
  nullableBoolean,
  nullableNumber,
  nullableNumberArray,
  nullableString,
} from './Common.schema';

/**
 * Schema for Deckline entries.
 * Extends BaseDeckManagementEntry to ensure consistent pk/sk handling.
 * Base schema for DeckLine that matches the API type structure.
 */
export const DecklineSchema = BaseDeckManagementEntry.extend({
  // TODO: Currently using defined basic types. Change these into more specific validations in the future.

  page_id: z.string(),
  deckline_id: z.string(),
  page_name: nullableString,
  kpi_id: nullableString,
  kpi_name: z.string().nullish(), // `nullableString` performs .trim() - we need to preserve blank space here.
  kpi_formula: nullableString,
  serial_no: nullableString,
  is_code_or_description: z.enum(CodeOrDescription).nullish(),
  is_format_bold: nullableBoolean,
  is_format_italics: nullableBoolean,
  is_format_underline: nullableBoolean,
  format_text_color: nullableString,
  format_cell_color: nullableString,
  format_cell_default: z.enum(CellFormat).nullish(),
  blankline_type: z.enum(BlanklineType).nullish(),
  is_format_borderline: nullableBoolean,
  footnotes: nullableNumberArray,
  subtotal_formula: nullableString,
  subtotal_parent: nullableString,
  is_calculated_kpi: z.enum(CalculatedKPIValues).nullish(),
  deckline_drivers: KPIDriversInputSchema,
  is_exception: nullableBoolean,
  page_section: nullableString,
  column_template: nullableString,
  adjcalcflag: nullableBoolean,
  exceptioncalculatedkpiflag: nullableBoolean,
  bordertopflag: nullableBoolean,
  borderrightflag: nullableBoolean,
  borderbottomflag: nullableBoolean,
  borderleftflag: nullableBoolean,
  borderweightflag: z.enum(BorderWeight).nullish(),
  textunderlineflag: nullableBoolean,
  denominator_kpi_id_for_pct: nullableString,
  denominator_kpi_id_drivers: KPIDriversInputSchema,
  denominator_is_calculated_kpi: z.enum(CalculatedKPIValues).nullish(),
  denominator_kpi_formula: nullableString,
  root_kpi_id: nullableString,
  kpi_variant_id: nullableString,
  display_name: nullableString,
  denominator_kpi_variant_id: nullableString,
  kpi_name_indent_level: nullableNumber,
  comments: z.array(CommentSchema).nullish(),
});

/**
 * Schema for creating new DeckLine entries.
 * Omits pk and sk fields as these are generated by the service.
 * Matches DecklineItemsInput GraphQL type requirements.
 */
export const CreateDecklineSchema = DecklineSchema.omit({
  pk: true,
  sk: true,
});

/**
 * Schema for updating existing Deckline entries.
 * Omits pk and sk fields as these are generated by the service.
 * Matches DecklineItemsInput GraphQL type requirements.
 */
export const UpdateDecklineSchema = DecklineSchema.omit({
  pk: true,
  sk: true,
});

/**
 * Schema for deleting existing Deckline entries
 * Includes only the primary fields needed for DecklineDeleteItemsInput
 */
export const DeleteDecklineSchema = z.object({
  page_id: z.string(),
  deckline_id: z.string(),
});

/**
 * Complete changeset schema for Deckline operations.
 * Provides create, update, and delete arrays with appropriate validation.
 */
export const DecklineChangeSetSchema = createChangeSetOperations({
  create: CreateDecklineSchema,
  update: UpdateDecklineSchema,
  delete: DeleteDecklineSchema,
});

/**
 * Type definitions for use in components and services
 */

/**
 * Type for Deckline entries in create operations (without pk/sk)
 */
export type CreateDecklineType = z.infer<typeof CreateDecklineSchema>;

/**
 * Type for Deckline entries in udpate operation (without pk/sk, or last_updated_date)
 */
export type UpdateDecklineType = z.infer<typeof UpdateDecklineSchema>;

/**
 * Type for Deckline entries in delete operations (with all fields)
 */
export type DeleteDecklineType = z.infer<typeof DeleteDecklineSchema>;

/**
 * Type for the complete Deckline changeset structure
 */
export type DecklineChangeSetType = z.infer<typeof DecklineChangeSetSchema>;
